import React, { useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { Badge, useBadges } from 'features/catalog/useBadges';
import { isBadgeReleased } from 'features/catalog/badge-release';
import { talosBadgesLogoURL } from 'config/constants';
import { prefetchImage } from 'shared/lib/image-prefetch';
import './LoginBadgeBackdrop.css';

const POP_INTERVAL_MS = 1700;
/** Leave a short quiet beat between waves. */
const POP_HOLD_MS = POP_INTERVAL_MS - 200;
/** Quiet beat after flash is enabled (login box in) before the first color pop. */
const FLASH_START_DELAY_MS = 200;
/** After flashing, a cell must sit out this many later flash waves. */
const FLASH_COOLDOWN_WAVES = 2;
/** Matches `--login-badge-gap` in CSS. */
const GAP_REM = 0;
/** Extra coverage for rotate(-6deg) + scale(1.12) + negative inset. */
const COVER_FACTOR = 1.45;
/**
 * Target ms between tile reveals on a warm cache.
 * Enforced as a real-time rate via rAF catch-up (not one timer per tile — browsers
 * clamp setTimeout and React can't commit that fast). Slow loads still pace via onload.
 */
const MIN_TILE_REVEAL_GAP_MS = 1;

/** URLs that have already loaded for the scrolling field — survive resize/remount. */
const warmBackdropUrls = new Set<string>();

type GridPos = { row: number; col: number };

type RevealSlot = {
  key: string;
  url: string;
};

function shuffle<T>(items: T[]): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

const fallbackTileToLogo = (e: SyntheticEvent<HTMLImageElement>) => {
  if (e.currentTarget.src !== talosBadgesLogoURL) {
    e.currentTarget.src = talosBadgesLogoURL;
  }
};

function posKey(row: number, col: number): string {
  return `${row}:${col}`;
}

/** Circular column distance — row is duplicated for the marquee, so first/last touch. */
function colDistance(a: number, b: number, rowWidth: number): number {
  const linear = Math.abs(a - b);
  if (rowWidth <= 2) return linear;
  return Math.min(linear, rowWidth - linear);
}

/** True if two tiles touch (including diagonally, including marquee wrap). */
function areAdjacent(a: GridPos, b: GridPos, rowWidth: number): boolean {
  if (a.row === b.row && a.col === b.col) return false;
  return Math.abs(a.row - b.row) <= 1 && colDistance(a.col, b.col, rowWidth) <= 1;
}

/** Pick up to `count` grid cells with no two adjacent. */
function pickScatteredPositions(positions: GridPos[], count: number, rowWidth: number): GridPos[] {
  const picked: GridPos[] = [];
  for (const candidate of shuffle(positions)) {
    if (picked.some((p) => areAdjacent(p, candidate, rowWidth))) continue;
    picked.push(candidate);
    if (picked.length >= count) break;
  }
  return picked;
}

/** Mirrors CSS: clamp(4.8875rem, 8.625vw, 7.1875rem) — 15% larger than the original tile. */
function tileSizePx(viewportWidth: number): number {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const min = 4.8875 * rem;
  const max = 7.1875 * rem;
  const preferred = 0.08625 * viewportWidth;
  return Math.min(max, Math.max(min, preferred));
}

function minColsForWidth(viewportWidth: number): number {
  return Math.max(8, Math.ceil(viewportWidth / tileSizePx(viewportWidth)) + 3);
}

function rowsNeededForHeight(height: number, viewportWidth: number): number {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const stride = tileSizePx(viewportWidth) + GAP_REM * rem;
  const coverHeight = height * COVER_FACTOR;
  return Math.max(6, Math.ceil(coverHeight / stride) + 1);
}

/** Middle band used for login color flashes. */
function middleBandRange(rowCount: number): { start: number; end: number } {
  const band = Math.min(6, rowCount);
  const start = Math.max(0, Math.floor((rowCount - band) / 2));
  return { start, end: start + band };
}

/**
 * Build a full-height backdrop grid (all requested rows — top through bottom).
 * Prefer global uniqueness; only reuse a badge when the deck can’t fill the grid,
 * and then place copies as far apart as possible (never twice in the same row).
 * Each row track is still duplicated for the seamless horizontal marquee.
 */
function buildRows(badges: Badge[], rowCount: number, viewportWidth: number): Badge[][] {
  if (badges.length === 0 || rowCount <= 0) return [];

  const pool = shuffle(badges);
  const minCols = minColsForWidth(viewportWidth);
  const uniqueCols = Math.floor(pool.length / rowCount);
  // Wide enough for marquee when possible; otherwise minCols and allow sparse reuse.
  const cols = Math.max(1, uniqueCols >= minCols ? uniqueCols : minCols);

  const grid: (Badge | null)[][] = Array.from({ length: rowCount }, () => Array.from({ length: cols }, () => null));
  const locations = new Map<string, GridPos[]>();

  const place = (badge: Badge, row: number, col: number) => {
    grid[row][col] = badge;
    const list = locations.get(badge.id) ?? [];
    list.push({ row, col });
    locations.set(badge.id, list);
  };

  const emptyCells = shuffle(
    Array.from({ length: rowCount * cols }, (_, i) => ({
      row: Math.floor(i / cols),
      col: i % cols,
    }))
  );

  // Pass 1 — one of each badge into random cells (no duplicates yet).
  let filled = 0;
  for (const badge of pool) {
    if (filled >= emptyCells.length) break;
    const { row, col } = emptyCells[filled++];
    place(badge, row, col);
  }

  // Pass 2 — remaining cells: reuse only when needed, maximize distance, never same row.
  for (; filled < emptyCells.length; filled++) {
    const { row, col } = emptyCells[filled];
    const rowIds = new Set(grid[row].filter((b): b is Badge => b != null).map((b) => b.id));

    let best: Badge | null = null;
    let bestScore = -1;
    for (const badge of pool) {
      if (rowIds.has(badge.id)) continue;
      const locs = locations.get(badge.id) ?? [];
      const minDist =
        locs.length === 0 ? 1_000 : Math.min(...locs.map((l) => Math.abs(l.row - row) * 4 + Math.abs(l.col - col)));
      if (minDist > bestScore) {
        bestScore = minDist;
        best = badge;
      }
    }

    place(best ?? pool[filled % pool.length], row, col);
  }

  return grid.map((row) => shuffle(row.filter((b): b is Badge => b != null)));
}

/** Matches the tile enter fade (`opacity` in CSS). */
const TILE_ENTER_MS = 450;

interface BackdropRowProps {
  row: Badge[];
  rowIndex: number;
  rowCount: number;
  mode: 'base' | 'flash';
  /** One char per column: '1' = revealed / thumb sourced / popped / feed-hot. */
  revealedMask: string;
  sourcedMask: string;
  poppedMask: string;
  hotMask: string;
}

const BackdropRow = React.memo(
  ({ row, rowIndex, rowCount, mode, revealedMask, sourcedMask, poppedMask, hotMask }: BackdropRowProps) => {
    const rowElevated = poppedMask.includes('1');
    const dirClass = rowIndex % 2 === 0 ? 'ltr' : 'rtl';
    return (
      <div
        className={`login-badge-row${rowElevated ? ' login-badge-row--elevated' : ''}`}
        style={{
          /* Upper rows stack above lower ones so scale-up isn’t covered underneath. */
          zIndex: rowCount - rowIndex + (rowElevated ? 1000 : 0),
        }}
      >
        <div
          className={`login-badge-row-motion login-badge-row-motion--${mode} login-badge-row--${dirClass}`}
          style={{ animationDuration: `${68 + (rowIndex % 6) * 11}s` }}
        >
          {[0, 1].map((copy) => (
            <div key={copy} className="login-badge-row-track">
              {row.map((badge, badgeIndex) => {
                const awaitingClass = revealedMask[badgeIndex] === '1' ? '' : ' login-badge-tile--awaiting';
                // Held back until the thumb is decoded, so the tag is a cache hit.
                const tileSrc = sourcedMask[badgeIndex] === '1' ? badge.smallImageUrl : undefined;
                const stateClass =
                  mode === 'flash'
                    ? ` login-badge-tile--flash${poppedMask[badgeIndex] === '1' ? ' popped' : ''}`
                    : hotMask[badgeIndex] === '1'
                      ? ' login-badge-tile--feed-hot'
                      : '';
                return (
                  <img
                    key={`${copy}-${badge.id}-${badgeIndex}`}
                    src={tileSrc}
                    alt=""
                    className={`login-badge-tile${stateClass}${awaitingClass}`}
                    decoding="async"
                    onError={fallbackTileToLogo}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }
);

interface LoginBadgeBackdropProps {
  /** Color “pops” — login only. Signed-in app usage stays muted. */
  enableFlash?: boolean;
  className?: string;
  /** Badge ids to color-pop in the field (e.g. feed card hover). */
  highlightBadgeIds?: ReadonlySet<string> | null;
  /** Fires when every tile has finished its enter animation (or reduced-motion skip). */
  onFieldReady?: (ready: boolean) => void;
}

/**
 * Full-bleed scrolling badge field.
 * Badges stay muted/grayscale; optionally a few periodically “pop” into color.
 * Tiles mount invisible and reveal as their thumbs load, paced by MIN_TILE_REVEAL_GAP_MS
 * so a fast cache can’t dump the whole field at once.
 */
export const LoginBadgeBackdrop: React.FC<LoginBadgeBackdropProps> = ({
  enableFlash = true,
  className,
  highlightBadgeIds = null,
  onFieldReady,
}) => {
  const { badgesMap } = useBadges();
  const rootRef = useRef<HTMLDivElement>(null);
  const [rowCount, setRowCount] = useState(8);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [poppedKeys, setPoppedKeys] = useState<Set<string>>(() => new Set());
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(() => new Set());
  /** Thumbs the shared queue has decoded — a tile only gets a `src` once its URL is in here. */
  const [sourcedUrls, setSourcedUrls] = useState<Set<string>>(() => new Set(warmBackdropUrls));
  const [flashReady, setFlashReady] = useState(false);
  const revealedKeysRef = useRef(revealedKeys);
  revealedKeysRef.current = revealedKeys;
  const revealGenerationRef = useRef(0);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    const shellBackdrop = node.classList.contains('app-shell-backdrop');
    const update = () => {
      // Battle fullscreen covers this field. Rebuilding it to the screen size is thrown away on exit.
      if (shellBackdrop && document.documentElement.classList.contains('bb-match-fullscreen')) return;
      const width = window.innerWidth;
      const nextRows = rowsNeededForHeight(node.clientHeight || window.innerHeight, width);
      setRowCount((prev) => (prev === nextRows ? prev : nextRows));
      // Only rebuild the badge grid when column coverage would change — not every resize px.
      setViewportWidth((prev) => (minColsForWidth(prev) === minColsForWidth(width) ? prev : width));
    };

    update();
    // Resizes arrive every frame while the window animates (e.g. leaving fullscreen); rebuild once it settles.
    let settleTimer = 0;
    const scheduleUpdate = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(update, 150);
    };
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(node);
    window.addEventListener('resize', scheduleUpdate);
    const classObserver = shellBackdrop ? new MutationObserver(scheduleUpdate) : null;
    classObserver?.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => {
      window.clearTimeout(settleTimer);
      observer.disconnect();
      classObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, []);

  const rows = useMemo(() => {
    const badges = Array.from(badgesMap.values()).filter((badge) => isBadgeReleased(badge.id));
    return buildRows(badges, rowCount, viewportWidth);
  }, [badgesMap, rowCount, viewportWidth]);

  /** Shuffled cell order — both marquee copies share a key and reveal together. */
  const revealSlots = useMemo((): RevealSlot[] => {
    const slots: RevealSlot[] = [];
    rows.forEach((row, rowIndex) => {
      row.forEach((badge, badgeIndex) => {
        slots.push({
          key: posKey(rowIndex, badgeIndex),
          url: badge.smallImageUrl,
        });
      });
    });
    return shuffle(slots);
  }, [rows]);

  // Progressive reveal: visibility is only via revealedKeys (min gap). Warm URLs skip
  // the network wait; resize rebuilds keep the field up. Tiles stay src-less until
  // their thumb is decoded so they paint as cache hits.
  useEffect(() => {
    if (revealSlots.length === 0) {
      setRevealedKeys(new Set());
      return;
    }

    const generation = ++revealGenerationRef.current;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Resize/rebuild (component still mounted) — keep warm tiles visible. Fresh mount/remount staggers.
    const isRebuild = revealedKeysRef.current.size > 0;

    const seed = new Set<string>();
    const pendingSlots: RevealSlot[] = [];
    for (const slot of revealSlots) {
      // Reduced motion skips the stagger — tiles just show up as their thumb arrives.
      if (reducedMotion || (isRebuild && warmBackdropUrls.has(slot.url))) seed.add(slot.key);
      else pendingSlots.push(slot);
    }
    setRevealedKeys(seed);

    const cancelledRef = { current: false };
    let raf = 0;
    let pumping = false;
    let nextRevealAt = 0;
    // Reveal in this shuffled slot order (not network completion order).
    const queue = pendingSlots.map((slot) => slot.key);
    const readyUrls = new Set<string>();
    const readyKeys = new Set<string>();
    const sourcedBatch: string[] = [];

    const commitKeys = (keys: string[]) => {
      if (keys.length === 0) return;
      setRevealedKeys((prev) => {
        const next = new Set(prev);
        for (const key of keys) next.add(key);
        return next;
      });
    };

    const commitSourced = () => {
      if (sourcedBatch.length === 0) return;
      const batch = sourcedBatch.splice(0, sourcedBatch.length);
      setSourcedUrls((prev) => {
        const added = batch.filter((url) => !prev.has(url));
        if (added.length === 0) return prev;
        const next = new Set(prev);
        for (const url of added) next.add(url);
        return next;
      });
    };

    const pump = (now: number) => {
      raf = 0;
      if (cancelledRef.current || generation !== revealGenerationRef.current) {
        pumping = false;
        return;
      }

      // Batched with the reveal below, so a tile's src lands in the same commit as its fade.
      commitSourced();

      if (readyKeys.size === 0 || queue.length === 0) {
        pumping = false;
        return;
      }

      if (nextRevealAt === 0) nextRevealAt = now;

      const gap = MIN_TILE_REVEAL_GAP_MS;
      const batch: string[] = [];
      while (queue.length > 0 && now >= nextRevealAt) {
        // Next tile in the random sequence whose image is ready — skip ahead only when waiting
        // would stall forever on a not-yet-ready URL while later ones are already decoded.
        let pickAt = -1;
        for (let i = 0; i < queue.length; i++) {
          if (readyKeys.has(queue[i])) {
            pickAt = i;
            break;
          }
        }
        if (pickAt < 0) break;

        const key = queue.splice(pickAt, 1)[0];
        readyKeys.delete(key);
        batch.push(key);
        nextRevealAt += gap;
        if (gap <= 0) {
          // Drain every ready key, still preferring earlier shuffle positions.
          while (queue.length > 0) {
            let nextReady = -1;
            for (let i = 0; i < queue.length; i++) {
              if (readyKeys.has(queue[i])) {
                nextReady = i;
                break;
              }
            }
            if (nextReady < 0) break;
            const readyKey = queue.splice(nextReady, 1)[0];
            readyKeys.delete(readyKey);
            batch.push(readyKey);
          }
          break;
        }
      }

      commitKeys(batch);

      if (queue.length > 0 && readyKeys.size > 0) {
        raf = window.requestAnimationFrame(pump);
      } else {
        pumping = false;
      }
    };

    const schedulePump = () => {
      if (pumping || cancelledRef.current) return;
      pumping = true;
      raf = window.requestAnimationFrame(pump);
    };

    const keysByUrl = new Map<string, string[]>();
    for (const slot of pendingSlots) {
      const keys = keysByUrl.get(slot.url) ?? [];
      keys.push(slot.key);
      keysByUrl.set(slot.url, keys);
    }

    const onUrlReady = (url: string) => {
      if (cancelledRef.current || generation !== revealGenerationRef.current) return;
      if (readyUrls.has(url)) return;
      readyUrls.add(url);
      warmBackdropUrls.add(url);
      sourcedBatch.push(url);
      for (const key of keysByUrl.get(url) ?? []) readyKeys.add(key);
      schedulePump();
    };

    // Queue every tile url — seeded ones included, since they still need a src.
    // Shuffled so cold-cache completion isn't catalog order either.
    const uniqueUrls = shuffle([...new Set(revealSlots.map((slot) => slot.url))]);
    for (const url of uniqueUrls) {
      // Already decoded this session — still goes through the gap pump, no network wait.
      if (warmBackdropUrls.has(url)) {
        onUrlReady(url);
        continue;
      }
      void prefetchImage(url).then(() => onUrlReady(url));
    }

    return () => {
      // In-flight prefetches stay running — those thumbs are wanted either way, and
      // prefetchImage dedupes them. cancelledRef is what stops this generation's state writes.
      cancelledRef.current = true;
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [revealSlots]);

  const revealComplete = revealSlots.length > 0 && revealSlots.every((slot) => revealedKeys.has(slot.key));

  // Tell parents when the field has finished animating in (last tile enter settles).
  useEffect(() => {
    if (!onFieldReady) return;
    if (!revealComplete) {
      onFieldReady(false);
      return;
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = window.setTimeout(() => onFieldReady(true), reduced ? 0 : TILE_ENTER_MS);
    return () => window.clearTimeout(timer);
  }, [revealComplete, onFieldReady]);

  useEffect(() => {
    setFlashReady(false);
    setPoppedKeys(new Set());
    if (!enableFlash || !revealComplete) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timer = window.setTimeout(() => setFlashReady(true), FLASH_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [enableFlash, revealComplete]);

  const flashPositions = useMemo(() => {
    if (!flashReady || rows.length === 0) return [] as GridPos[];
    const { start, end } = middleBandRange(rows.length);
    const positions: GridPos[] = [];
    for (let rowIndex = start; rowIndex < end; rowIndex++) {
      rows[rowIndex].forEach((_, colIndex) => {
        positions.push({ row: rowIndex, col: colIndex });
      });
    }
    return positions;
  }, [rows, flashReady]);

  const popCount = 6;

  useEffect(() => {
    if (!flashReady || flashPositions.length === 0) {
      setPoppedKeys(new Set());
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let holdTimer: number | undefined;
    const cooldown = new Map<string, number>();

    const tick = () => {
      if (holdTimer) window.clearTimeout(holdTimer);

      const rowWidth = flashPositions.reduce((max, p) => Math.max(max, p.col + 1), 0);
      // Only flash cells that have already faded in.
      const revealed = revealedKeysRef.current;
      const eligible = flashPositions.filter(
        (p) => revealed.has(posKey(p.row, p.col)) && !cooldown.has(posKey(p.row, p.col))
      );
      const picked = pickScatteredPositions(eligible, popCount, rowWidth);
      const next = new Set(picked.map((p) => posKey(p.row, p.col)));

      for (const [key, remaining] of [...cooldown.entries()]) {
        if (remaining <= 1) cooldown.delete(key);
        else cooldown.set(key, remaining - 1);
      }
      for (const key of next) {
        cooldown.set(key, FLASH_COOLDOWN_WAVES);
      }

      setPoppedKeys(next);
      holdTimer = window.setTimeout(() => setPoppedKeys(new Set()), POP_HOLD_MS);
    };

    tick();
    const interval = window.setInterval(tick, POP_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
      if (holdTimer) window.clearTimeout(holdTimer);
    };
  }, [flashPositions, popCount, flashReady]);

  const rootClass = ['login-badge-backdrop', className].filter(Boolean).join(' ');

  // Grayscale under the scrim (dark washed field); color pops above it.
  // Base and flash rows run the same CSS marquee; every marquee is pinned to the
  // document timeline origin so a row's two layers stay 1:1 whenever either (re)starts.
  const alignMarquee = (e: React.AnimationEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('login-badge-row-motion')) return;
    for (const animation of target.getAnimations()) animation.startTime = 0;
  };

  // Per-row masks ('1' per column) — string props let unchanged rows skip re-rendering.
  const rowMask = (row: Badge[], test: (badge: Badge, col: number) => boolean) =>
    row.map((badge, col) => (test(badge, col) ? '1' : '0')).join('');

  const renderRows = (mode: 'base' | 'flash') =>
    rows.map((row, rowIndex) => (
      <BackdropRow
        key={`${mode}-${rowIndex}`}
        row={row}
        rowIndex={rowIndex}
        rowCount={rows.length}
        mode={mode}
        revealedMask={rowMask(row, (_, col) => revealedKeys.has(posKey(rowIndex, col)))}
        sourcedMask={rowMask(row, (badge) => sourcedUrls.has(badge.smallImageUrl))}
        poppedMask={mode === 'flash' ? rowMask(row, (_, col) => poppedKeys.has(posKey(rowIndex, col))) : ''}
        hotMask={mode === 'base' && highlightBadgeIds ? rowMask(row, (badge) => highlightBadgeIds.has(badge.id)) : ''}
      />
    ));

  if (badgesMap.size === 0) {
    return <div ref={rootRef} className={rootClass} aria-hidden="true" />;
  }

  return (
    <div ref={rootRef} className={rootClass} aria-hidden="true" onAnimationStart={alignMarquee}>
      <div className="login-badge-backdrop-rows">{renderRows('base')}</div>
      <div className="login-badge-backdrop-scrim" />
      {enableFlash ? <div className="login-badge-backdrop-flashes">{renderRows('flash')}</div> : null}
    </div>
  );
};
